import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch, getStoredUser } from '../api';
import { useDemoMode } from '../context/DemoModeContext';
import { ROSTER_SELECTED_TUTOR_SCOPE_KEY } from '../constants/rosterScope';
import { getStudentDisplayName } from '../utils/studentName';

const readStoredTutorScope = () => {
  try {
    const localValue = localStorage.getItem(ROSTER_SELECTED_TUTOR_SCOPE_KEY);
    if (localValue) return localValue;
  } catch {}
  try {
    const sessionValue = sessionStorage.getItem(ROSTER_SELECTED_TUTOR_SCOPE_KEY);
    if (sessionValue) return sessionValue;
  } catch {}
  return null;
};

const normalizeStudents = (students) => {
  return (students || []).map((student) => ({
    ...student,
    first_name: student?.first_name || null,
    last_name: student?.last_name || null,
    display_name: getStudentDisplayName(student)
  }));
};

const useScopedStudents = ({ skip = false } = {}) => {
  const { getEffectiveRole } = useDemoMode();
  const effectiveRole = getEffectiveRole();

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getCurrentTutorProfileId = useCallback(async () => {
    const cachedUser = getStoredUser();
    let tutorId = cachedUser?.profile_id || null;
    if (tutorId) return tutorId;

    const meResponse = await apiFetch('/api/auth/me');
    if (!meResponse.ok) return null;
    const meData = await meResponse.json();
    tutorId = meData?.profile_id || null;
    return tutorId;
  }, []);

  const fetchAllStudents = useCallback(async () => {
    const response = await apiFetch('/api/admin/all-students');
    if (!response.ok) throw new Error('Failed to fetch all students');
    const data = await response.json();
    return normalizeStudents(data.students || []);
  }, []);

  const fetchTutorRoster = useCallback(async (tutorId) => {
    const response = await apiFetch(`/api/student-roster?tutor_id=${tutorId}`);
    if (!response.ok) throw new Error('Failed to fetch student roster');
    const data = await response.json();
    return normalizeStudents(data.students || []);
  }, []);

  const loadStudents = useCallback(async () => {
    if (skip) return;
    setLoading(true);
    setError(null);

    try {
      const assignedOnlyMode = effectiveRole === 'team_captain';

      if (assignedOnlyMode) {
        const tutorId = await getCurrentTutorProfileId();
        if (!tutorId) {
          setStudents([]);
          return;
        }
        const rosterStudents = await fetchTutorRoster(tutorId);
        setStudents(rosterStudents);
        return;
      }

      if (effectiveRole === 'admin') {
        const storedScope = readStoredTutorScope();
        if (!storedScope || storedScope === 'all') {
          const allStudents = await fetchAllStudents();
          setStudents(allStudents);
          return;
        }

        const scopedTutorId = Number.parseInt(storedScope, 10);
        if (!Number.isInteger(scopedTutorId) || scopedTutorId <= 0) {
          const allStudents = await fetchAllStudents();
          setStudents(allStudents);
          return;
        }

        try {
          const scopedStudents = await fetchTutorRoster(scopedTutorId);
          setStudents(scopedStudents);
        } catch {
          // Fallback for stale/invalid persisted scope.
          const allStudents = await fetchAllStudents();
          setStudents(allStudents);
        }
        return;
      }

      setStudents([]);
    } catch (err) {
      console.error('Error loading scoped students:', err);
      setStudents([]);
      setError(err.message || 'Failed to load students');
    } finally {
      setLoading(false);
    }
  }, [skip, effectiveRole, getCurrentTutorProfileId, fetchAllStudents, fetchTutorRoster]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  const studentMap = useMemo(() => {
    const map = new Map();
    students.forEach((student) => {
      map.set(student.uuid, student);
    });
    return map;
  }, [students]);

  return {
    students,
    studentsLoading: loading,
    studentsError: error,
    refetchStudents: loadStudents,
    studentMap
  };
};

export default useScopedStudents;
